import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/index';
import { filter, map, take, tap } from 'rxjs/operators';
import { AuthenticationActions } from './state/authentication.actions';
import { AuthState } from './state/authentication.reducer';
import { AuthenticationSelectors } from './state/authentication.selectors';
import { AccountService } from '../core/services/account.service';
import { APP_ROUTES } from '../app-routing.module';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuard implements CanActivate {
    authenticate$: Observable<AuthState>;
    enrollmentUrl = 'http://www.autoremind.com/emds/registration';
    isInIframe: boolean;

    private _ROUTES_NG1_ALLOWED =['reports/missed', 'reports/recalls' , 'reports/contacts', 'reports/unsubscribe', 'reports/messaging'];

    constructor(private authenticationSelectors: AuthenticationSelectors,
                private router: Router,
                private accountService: AccountService,
                private store: Store<any>) {
        this.authenticate$ = this.authenticationSelectors.selectAuthenticate();
        this.isInIframe = window.self !== window.top;
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        console.log('Can Activate :::::::   ', state.url);

        return this.authenticate$
            .pipe(
                filter(authenticate => !authenticate.loading), // If login is already in progress dont call login again
                map(authenticate => {
                    if (authenticate.user && authenticate.isUserAuthenticated) {

                        // Check if User can access rights for URL
                        let hasPermission = false;
                        // #182790912 when the navigation start in Ng1 part as a child route
                        let isChildRouteForNg1 = false;
                        let returnUrl = state.url.split('?')[0];
                        let stateUrl = returnUrl.endsWith('/') ? state.url.substr(0, returnUrl.length -1) : returnUrl;
                        stateUrl = stateUrl.startsWith('/') ? stateUrl.substr(1, stateUrl.length) : stateUrl;
                        // in case of lazy loading the path is not in APP_ROUTES
                        stateUrl = stateUrl.indexOf('patients') !== -1
                                    || stateUrl.indexOf('reminders') !== -1
                                    || stateUrl.indexOf('clients') !== -1
                                     && stateUrl.indexOf('admin') === -1
                            ?
                            stateUrl.substr(0, stateUrl.indexOf('/')) :
                            stateUrl.indexOf('users/find') === -1 ?
                            stateUrl : 'admin/users/find';


                        const router  = APP_ROUTES.find(router => stateUrl ===  router.path.toString());
                        if (Boolean(router)) {
                            hasPermission = this.accountService.hasUserPermission(router, authenticate.user);
                        }
                        else {
                          // #182790912 when the navigation start in Ng1 part as a child route
                            isChildRouteForNg1 = Boolean(this._ROUTES_NG1_ALLOWED.find(routeNg1 => stateUrl === routeNg1));
                        }

                        if (!hasPermission && !isChildRouteForNg1) {
                            this.router.navigate(['/dashboard']);
                        }

                        return true;
                    }

                    // navigate to enrollmentURL if it includes system
                    if (route.queryParams['system'] !== undefined && route.queryParams['system'] === 'emds'
                        && (route.queryParams['username'] === undefined || route.queryParams['username'] === '')) {
                        // delete params.system; // This is pointless as we're redirecting to different site
                        // For external url we need to use it this way
                        // TODO: Check if there is any other way to redirect to external URL
                        window.location.href = (this.enrollmentUrl);

                        return false;
                    }

                    return this.autoLoginOrRedirectToLoginPage(route, authenticate, state.url);
                })
            );
    }

    private autoLoginOrRedirectToLoginPage(route, authenticate, stateUrl): boolean {
        const queryParams = { ...route.queryParams };

        // returnUrl here based on route just requested
        // Strip parameters as we'll manage them separately
        let returnUrl = stateUrl.split('?')[0];

        // TODO: Remove option for using localStorage storage - get from NgRx
        const username = queryParams.username || localStorage.getItem('username');
        const password = queryParams.password || localStorage.getItem('password');
        const rememberMe = queryParams.rememberMe || JSON.parse(localStorage.getItem('rememberMe'));

        // For Admin and login via MacPractice, Medisoft, Lytec,... where username and password comes from URL
        if (username !== undefined && username !== null) {
            // if new user logs in with username and password in URL (and not Admin URL)
            if (authenticate.user !== undefined && authenticate.user !== username && !authenticate.isAdmin) {
                this.store.dispatch(AuthenticationActions.logoutUser());
            }

            const isAuthenticatedFromAdmin = queryParams.admin || false;
            const accountAccessToken = queryParams.aat;

            // Removing all params from url
            delete queryParams.username;
            delete queryParams.password;
            delete queryParams.admin;
            delete queryParams.page;
            delete queryParams.aat;

            if (queryParams.remotepatientid !== undefined && queryParams.remotepatientid !== '') {
                returnUrl = '/patients/appointments';
            }
            if (queryParams.remoteappointmentid !== undefined && queryParams.remoteappointmentid !== '') {
                returnUrl = '/appointments';
            }

            if (queryParams.remotepatientid !== undefined && queryParams.remotepatientid !== '' &&
                queryParams.remoteappointmentid !== undefined && queryParams.remoteappointmentid !== '') {
                returnUrl = '/patients/appointments';
            }

            if (queryParams.appointmentid !== undefined && queryParams.appointmentid !== '') {
                returnUrl = '/appointments';
            }

            if (queryParams.patientid !== undefined && queryParams.patientid !== '' &&
                queryParams.appointmentid !== undefined && queryParams.appointmentid !== '') {
                returnUrl = '/patients/appointments';
            }

            this.store.dispatch(AuthenticationActions.loginUser({
                username,
                password,
                rememberMe,
                returnUrl, // used by effect doing redirect after loginUserSuccessful
                isAuthenticatedFromAdmin,
                returnUrlPathParams: queryParams,
                accountAccessToken
            }));

        } else {
            // Not logged in so redirect to login page
            this.router.navigate(['login'], {state: {returnUrl}, queryParams});
            // The returnUrl is read by the LoginComponent using navigation.extras.state.returnUrl
        }

        return false;
    }

}
